import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/new/components/shadcn/popover';

interface DateRangeFilterProps {
  startDate: Date | null;
  endDate: Date | null;
  setStartDate: (startDate: Date | null) => void;
  setEndDate: (endDate: Date | null) => void;
}

const formatDate = (date: Date | null) => {
  if (!date) return '';
  return date.toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// Reusable popover for date selection
interface DatePopoverProps {
  label: string;
  buttonLabel: string;
  date: Date | null;
  tempDate: Date | null;
  setTempDate: (date: Date | null) => void;
  onApply: () => void;
  onClear: () => void;
  popoverOpen: boolean;
  setPopoverOpen: (open: boolean) => void;
}

const DatePopover = ({
  label,
  buttonLabel,
  date,
  tempDate,
  setTempDate,
  onApply,
  onClear,
  popoverOpen,
  setPopoverOpen,
}: DatePopoverProps) => {
  const { i18n } = useTranslation();

  return (
    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
      <PopoverTrigger asChild>
        <div className="flex items-center text-xs">
          <button className="flex items-center rounded border px-2 py-1.5 text-gray-600 transition-colors duration-200 hover:bg-gray-50">
            {date
              ? `${buttonLabel} ${new Date(date).toLocaleDateString(i18n.language, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })}`
              : buttonLabel}
            <Icon className="lucide--chevron-down ml-1" />
          </button>
        </div>
      </PopoverTrigger>
      <PopoverContent className="z-50 w-80 rounded-lg border border-gray-200 bg-white p-4 shadow-lg">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-600">{label}</label>
            <input
              type="date"
              lang={i18n.language}
              value={tempDate?.toISOString().split('T')[0] || ''}
              onChange={(e) => setTempDate(e.target.value ? new Date(e.target.value) : null)}
              className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-xs focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onApply();
                }
              }}
            />
          </div>
          <div className="h-px bg-gray-100" />
          <div className="flex items-center justify-between">
            <Button variant="destructive" onClick={onClear} className="text-xs">
              Clear
            </Button>
            <Button variant="outline" onClick={onApply} className="text-xs">
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const DateRangeFilter = ({ startDate, endDate, setStartDate, setEndDate }: DateRangeFilterProps) => {
  const [tempStartDate, setTempStartDate] = useState<Date | null>(startDate);
  const [tempEndDate, setTempEndDate] = useState<Date | null>(endDate);
  const [startPopoverOpen, setStartPopoverOpen] = useState(false);
  const [endPopoverOpen, setEndPopoverOpen] = useState(false);

  const handleApplyStart = () => {
    setStartDate(tempStartDate);
    setStartPopoverOpen(false);
    trackCustomEvent(TrackingEventsV2.FactFilterByDate);
  };

  const handleApplyEnd = () => {
    setEndDate(tempEndDate);
    setEndPopoverOpen(false);
    trackCustomEvent(TrackingEventsV2.FactFilterByDate);
  };

  const handleClearStart = () => {
    setTempStartDate(null);
    setStartDate(null);
    setStartPopoverOpen(false);
  };

  const handleClearEnd = () => {
    setTempEndDate(null);
    setEndDate(null);
    setEndPopoverOpen(false);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-4">
        <DatePopover
          label="After:"
          buttonLabel="After"
          date={startDate}
          tempDate={tempStartDate}
          setTempDate={setTempStartDate}
          onApply={handleApplyStart}
          onClear={handleClearStart}
          popoverOpen={startPopoverOpen}
          setPopoverOpen={setStartPopoverOpen}
        />
        <DatePopover
          label="Before:"
          buttonLabel="Before"
          date={endDate}
          tempDate={tempEndDate}
          setTempDate={setTempEndDate}
          onApply={handleApplyEnd}
          onClear={handleClearEnd}
          popoverOpen={endPopoverOpen}
          setPopoverOpen={setEndPopoverOpen}
        />
      </div>
    </div>
  );
};
